import React from "react";
import { Clock } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { TESSERA_BRAND_LOGO_WHITE_SRC } from "@/constants/sidebarBrandLogo";
import { cn } from "@/lib/utils";

import {
  universityCourseLessonCardClassName,
  universityCourseThumbFallbackClassName,
} from "./universityCardShell";
import { universityCourseMutedTextClassName } from "./courseOverviewCardShell";
import { UniversityCourseStatusBadge } from "./UniversityCourseStatusBadge";

const LESSON_FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]";
const LESSON_FIELD_DESC_CLASS = cn(
  universityCourseMutedTextClassName,
  "text-xs leading-5",
);

const LESSON_THUMB_GRADIENT = "linear-gradient(135deg, #38BBD1 0%, #3EEAEC 100%)";

export type UniversityCourseLessonCardModel = {
  title: string;
  description: string;
  /** @deprecated Lesson thumbs always use gradient + brand icon; ignored if set. */
  imageSrc?: string;
  imageGradient?: string;
  readingTimeLabel: string;
};

type UniversityCourseLessonCardProps = {
  lesson: UniversityCourseLessonCardModel;
  completed: boolean;
  selected?: boolean;
  compact?: boolean;
  onClick: () => void;
};

export function UniversityCourseLessonCard({
  lesson,
  completed,
  selected = false,
  compact = false,
  onClick,
}: UniversityCourseLessonCardProps): React.ReactElement {
  const imageGradient = lesson.imageGradient ?? LESSON_THUMB_GRADIENT;

  const thumbClassName = compact
    ? "relative flex h-full w-[7rem] shrink-0 items-center justify-center rounded-none rounded-l-[6px]"
    : cn(
        universityCourseThumbFallbackClassName,
        "relative flex items-center justify-center sm:h-[7.5rem] sm:w-40 sm:max-w-[10rem] sm:rounded-none",
      );

  return (
    <button
      type="button"
      data-university-course-lesson=""
      {...(compact ? { "data-university-course-lesson-compact": "" } : {})}
      className={cn(
        "w-full rounded-[6px] text-left outline-none",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      )}
      onClick={onClick}
    >
      <Card
        className={cn(
          universityCourseLessonCardClassName,
          "transition-colors hover:bg-muted/30",
          selected && "bg-muted/30",
        )}
      >
        <div
          className={cn(
            "flex min-w-0",
            compact ? "h-[5.75rem] flex-row items-stretch" : "flex-col sm:flex-row sm:items-stretch",
          )}
        >
          <div
            data-university-course-lesson-thumb=""
            className={thumbClassName}
            style={{ backgroundImage: imageGradient }}
            aria-hidden
          >
            <img
              src={TESSERA_BRAND_LOGO_WHITE_SRC}
              alt=""
              draggable={false}
              className={cn(
                "object-contain opacity-95",
                compact ? "size-8" : "size-10 sm:size-11",
              )}
            />
          </div>

          <CardContent
            className={cn(
              "flex min-w-0 flex-1 flex-col overflow-hidden",
              compact ? "justify-center gap-1 p-2.5" : "gap-3 p-4",
            )}
          >
            <Field className={compact ? "min-w-0 gap-0.5" : "gap-1.5"}>
              <div
                className={cn(
                  "flex min-w-0 gap-2",
                  compact ? "items-center" : "flex-wrap items-start justify-between",
                )}
              >
                <FieldLabel
                  className={cn(
                    LESSON_FIELD_LABEL_CLASS,
                    compact &&
                      "min-w-0 flex-1 basis-0 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] leading-5 !w-auto",
                  )}
                >
                  {lesson.title}
                </FieldLabel>
                {completed ? (
                  <UniversityCourseStatusBadge tone="completed" className="shrink-0">
                    Completed
                  </UniversityCourseStatusBadge>
                ) : null}
              </div>
              <FieldDescription
                className={cn(
                  LESSON_FIELD_DESC_CLASS,
                  compact && "truncate text-[11px] leading-4",
                )}
                data-university-course-muted=""
              >
                {lesson.description}
              </FieldDescription>
            </Field>

            <div
              data-university-course-lesson-meta=""
              className={cn(
                "inline-flex min-w-0 items-center gap-1.5 text-xs",
                universityCourseMutedTextClassName,
                compact && "shrink-0 text-[11px]",
              )}
            >
              <Clock
                className="size-3.5 shrink-0 text-[color:var(--brand-tropical-surf)]"
                aria-hidden
              />
              <span data-university-course-muted="">{lesson.readingTimeLabel}</span>
            </div>
          </CardContent>
        </div>
      </Card>
    </button>
  );
}
